import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { ArrowLeft } from 'lucide-react';
import api from '../../utils/api';
import { createPortal } from 'react-dom';


const Loader = () => createPortal(
  <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-lime-400"></div>
    <p className="text-white ml-4">Saving...</p>
  </div>,
  document.body
);

const EditProfileScreen = ({ onBack, user, setUser }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImagePreview, setCroppedImagePreview] = useState(null);
  const [banner, setBanner] = useState({ show: false, message: '', type: 'success' });

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImage = useCallback(async () => {
    try {
      if (!croppedAreaPixels || !imageSrc) return;

      const image = await createImage(imageSrc);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = 200;
      canvas.height = 200;

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0, 0, 200, 200
      );

      const croppedImageUrl = canvas.toDataURL("image/jpeg", 0.8);
      setCroppedImagePreview(croppedImageUrl);

      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], "profile.jpg", { type: "image/jpeg" });

      setUser(prev => ({ ...prev, photoFile: file }));
      setCropOpen(false);
      setImageSrc(null);
    } catch (e) {
      console.error("Error cropping image", e);
    }
  }, [croppedAreaPixels, imageSrc, setUser]);

  useEffect(() => {
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc);
      if (croppedImagePreview) URL.revokeObjectURL(croppedImagePreview);
    };
  }, [imageSrc, croppedImagePreview]);

  const handleSaveProfile = async () => {
    setBanner({ message: '', type: '', show: false });
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('FirstName', user.firstname);
      formData.append('LastName', user.lastname);
      formData.append('Gender', user.gender);
      formData.append('Phone', user.phone);
      formData.append('Email', user.email);
      formData.append('DateOfBirth', user.dateOfBirth || '');

      const res = await api.post('/api/Profile/updateProfile', formData);

      if (res.status === 200) {
        const data = res.data;
        setIsLoading(false);
        setBanner({ message: 'Profile updated successfully!', type: 'success', show: true });

        setUser(prev => ({
          ...prev,
          photoUrl: data.photoUrl,
          photoFile: null,
          firstname: data.firstName || prev.firstname,
          lastname: data.lastName || prev.lastname,
          gender: data.gender || prev.gender,
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : "",
          phone: data.phoneNumber || prev.phone,
          email: data.email || prev.email,
          name: `${data.firstName || prev.firstname} ${data.lastName || prev.lastname}`,
        }));

        setCroppedImagePreview(null);
        setTimeout(() => setBanner({ message: '', type: '', show: false }), 3000);
      } else {
        setIsLoading(false);
        const error = await res.text();
        setBanner({ message: `Failed to update profile: ${error}`, type: 'error', show: true });
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error updating profile:', error);
      setBanner({ message: 'Unexpected error. Please try again later.', type: 'error', show: true });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImageSrc(url);
      setUser(prev => ({ ...prev, photoFile: file }));
      setCroppedImagePreview(null);
      setCropOpen(true);
    }
  };

  return (
    <div className="max-w-[480px] mx-auto pt-2 pb-4">
      <style>{`
        @keyframes editprofile-slideIn {
          from { opacity: 0; transform: translate(-50%, -10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
      {isLoading && <Loader />}

      {/* Banner */}
      {banner.show && (
        <div
          className={`fixed top-2.5 left-1/2 -translate-x-1/2 z-[1000] w-[90%] max-w-[380px] p-3.5 rounded-[12px] border backdrop-blur-[8px] ${banner.type === 'success'
            ? 'bg-green-500/15 border-green-500/40 text-green-200'
            : 'bg-red-500/15 border-red-500/40 text-red-200'
            }`}
          style={{ animation: 'editprofile-slideIn 0.3s ease-out' }}
        >
          <div className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${banner.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}>
              {banner.type === 'success' ? '✓' : '✕'}
            </div>
            <span className="text-[13px] font-medium flex-1">{banner.message}</span>
            <button
              onClick={() => setBanner({ message: '', type: '', show: false })}
              className="bg-transparent border-none text-white/70 cursor-pointer p-0.5 rounded-full text-[12px] transition-all hover:bg-white/15 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={onBack}
          className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all"
        >
          <ArrowLeft size={18} />
        </button>
        <span className="text-[20px] font-medium text-white">Edit Profile</span>
      </div>

      {/* Avatar */}
      <div className="flex flex-col items-center gap-1.5 mt-2 mb-3 py-1">
        <div className="w-20 h-20 rounded-full bg-[#6CD800] flex items-center justify-center overflow-hidden text-2xl font-bold text-[#181f10] relative">
          {croppedImagePreview ? (
            <img src={croppedImagePreview} alt="Preview" className="w-full h-full object-cover" />
          ) : user.photoUrl ? (
            <img src={user.photoUrl.replace(/\\/g, '/')} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            user.firstname?.charAt(0) || '?'
          )}
        </div>
        <label htmlFor="editprofile-file-upload" className="text-[12px] text-[#6CD800] cursor-pointer bg-transparent border-none py-1 px-3 rounded-[20px] transition-colors hover:bg-[#6CD800]/10">
          Change Photo
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id="editprofile-file-upload"
          style={{ display: 'none' }}
        />
      </div>

      {/* Cropped Image Preview */}
      {croppedImagePreview && (
        <div className="mb-5">
          <div className="flex items-center gap-3 mt-2.5 p-2.5 bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[10px]">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-[#333] shrink-0">
              <img src={croppedImagePreview} alt="Cropped" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="text-[13px] font-medium text-white max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">profile photo</div>
              <button
                className="text-[11px] text-red-500 bg-transparent border-none cursor-pointer p-0 mt-0.5 hover:text-red-400"
                onClick={() => {
                  setCroppedImagePreview(null);
                  setUser(prev => ({ ...prev, photoFile: null }));
                  document.getElementById('editprofile-file-upload').click();
                }}
              >
                Change Photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File info */}
      {!croppedImagePreview && (user.photoFile || user.photoUrl) && !cropOpen && (
        <div className="text-center mb-4 text-[12px] text-[#888]">
          {user.photoFile?.name || ''}
        </div>
      )}

      {/* Form Fields */}
      <div className="text-[11px] font-medium text-[#888] uppercase tracking-[0.6px] mb-1.5">Personal Information</div>
      <div className="bg-[#101010] border-[0.5px] border-[#2b2b2b] rounded-[12px] overflow-hidden mb-4">
        <div className="p-2.5 border-b-[0.5px] border-[#2b2b2b]">
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">First Name</label>
          <input
            type="text"
            className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 text-[14px] text-white outline-none transition-colors focus:border-[#6CD800]"
            value={user.firstname}
            onChange={(e) => setUser(prev => ({ ...prev, firstname: e.target.value }))}
          />
        </div>

        <div className="p-2.5 border-b-[0.5px] border-[#2b2b2b]">
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">Last Name</label>
          <input
            type="text"
            className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 text-[14px] text-white outline-none transition-colors focus:border-[#6CD800]"
            value={user.lastname}
            onChange={(e) => setUser(prev => ({ ...prev, lastname: e.target.value }))}
          />
        </div>

        <div className="p-2.5 border-b-[0.5px] border-[#2b2b2b]">
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">Gender</label>
          <select
            className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 text-[14px] text-white outline-none transition-colors focus:border-[#6CD800] appearance-none cursor-pointer"
            value={user.gender}
            onChange={(e) => setUser(prev => ({ ...prev, gender: e.target.value }))}
          >
            <option value="">--Select--</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="p-2.5 border-b-[0.5px] border-[#2b2b2b]">
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">Date of Birth (optional)</label>
          <input
            type="date"
            className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 text-[14px] text-white outline-none transition-colors focus:border-[#6CD800] appearance-none cursor-pointer"
            value={user.dateOfBirth || ''}
            onChange={(e) => setUser(prev => ({ ...prev, dateOfBirth: e.target.value }))}
          />
          <div className="text-[10px] text-[#888] mt-1">
            Helps us personalize your experience
          </div>
          <div className="text-[10px] text-gray-500/80 mt-1 italic leading-relaxed">
            By adding your date of birth, you agree to its use as described in our Privacy Policy.
          </div>
        </div>

        <div className="p-2.5 border-b-[0.5px] border-[#2b2b2b]">
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">Email</label>
          <input
            type="email"
            className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 text-[14px] text-white outline-none transition-colors focus:border-[#6CD800]"
            value={user.email || ''}
            onChange={(e) => setUser(prev => ({ ...prev, email: e.target.value }))}
          />
        </div>

        <div className="p-2.5 last:border-b-0">
          <label className="block text-[11px] font-medium text-[#888] uppercase tracking-[0.4px] mb-1">Phone</label>
          <input
            type="tel"
            className="w-full bg-[#0d0d0d] border-[0.5px] border-[#1a1a1a] rounded-[8px] p-2.5 text-[14px] text-[#666] outline-none cursor-not-allowed"
            value={user.phone}
            readOnly
          />
        </div>
      </div>

      {/* Save Button */}
      <button
        className="w-full bg-[#6CD800] text-[#181f10] border-none rounded-[10px] p-3 text-[14px] font-semibold cursor-pointer transition-opacity mt-4 hover:opacity-90"
        onClick={handleSaveProfile}
      >
        Save Profile
      </button>

      {/* CROPPING MODAL */}
      {cropOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-[#222] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="relative h-96 w-full mb-4 bg-gray-800 rounded-lg">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropDoubleClick={() => setZoom(1)}
                onZoomChange={setZoom}
                onMediaLoaded={() => setCroppedAreaPixels(null)}
                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setCropOpen(false);
                  setImageSrc(null);
                  setUser(prev => ({ ...prev, photoFile: null }));
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={getCroppedImage}
                className="px-6 py-2 bg-[#6CD800] hover:bg-[#6CD800]/90 text-[#181f10] rounded-lg font-medium disabled:opacity-50"
                disabled={!croppedAreaPixels}
              >
                Crop & Use
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfileScreen;
